import { createContext, useContext } from 'react'
import type { DocSyncIndicatorState } from '@/shared/hooks/useOfficePageContext'

/**
 * Authenticated-shell state shared with the chrome that frames the screens
 * (the top action bar and the doc-sync pill). Provided by the app shell so
 * those shared components stay self-contained instead of being prop-drilled.
 */
export interface ShellContextValue {
  /** Live document-sync indicator state (Word only; 'disabled' elsewhere). */
  syncState: DocSyncIndicatorState
  /** Whether the scanner screen is currently shown. */
  showScan: boolean
  /** Whether scanning is available on this host (Word). */
  canScan: boolean
  /** Toggle between the chat and scan screens. */
  toggleView: () => void
  /** Sign the current user out. */
  signOut: () => void
}

const ShellContext = createContext<ShellContextValue | null>(null)

export const ShellProvider = ShellContext.Provider

/** Read the app-shell context; throws if used outside a ShellProvider. */
export function useShell(): ShellContextValue {
  const ctx = useContext(ShellContext)
  if (!ctx) throw new Error('useShell must be used within a ShellProvider')
  return ctx
}
